/**
 * Heinrich 블로그 — 게시물 레지스트리.
 * 네이버 블로그(phase*)에서 이전한 글을 자체 도메인(/blog)에 발행.
 * 자체 도메인 발행 = 제품 도메인 SEO 권위 축적 + AI 검색(GEO) 인용 대상.
 *
 * 콘텐츠는 렌더링 블록 배열로 구조화 → BlogPost.tsx 가 디자인 시스템에 맞춰 렌더.
 */

export type Block =
  | { type: 'p'; html: string }
  | { type: 'h2'; text: string }
  | { type: 'img'; src: string; caption: string }
  | { type: 'ul'; items: string[] }
  | { type: 'callout'; label: string; items: string[] }
  | { type: 'pullquote'; text: string }
  | { type: 'cta'; title: string; body: string; label: string; href: string }

export interface BlogPost {
  slug: string
  lang: 'ko' | 'en'
  category: string
  title: string
  standfirst: string
  date: string          // YYYY.MM.DD (정렬용 — hideDate 시 화면·구조화데이터 미노출)
  readMin: number
  cover: string         // /blog/<slug>/<file>
  featured?: boolean
  hideDate?: boolean    // 이번 일괄 배포분은 실제 발행일과 달라 날짜 숨김(기본 true)
  blocks: Block[]
}

import { phase1 } from './posts/phase1'
import { phase2 } from './posts/phase2'
import { phase3 } from './posts/phase3'
import { phase4 } from './posts/phase4'
import { phase5 } from './posts/phase5'
import { phase6 } from './posts/phase6'
import { phase7 } from './posts/phase7'
import { phase8 } from './posts/phase8'
import { phase9 } from './posts/phase9'
import { phase10 } from './posts/phase10'
import { phase11 } from './posts/phase11'
import { phase12 } from './posts/phase12'
import { phase13 } from './posts/phase13'
import { phase14 } from './posts/phase14'
import { phase15 } from './posts/phase15'
import { phase16 } from './posts/phase16'
import { phase17 } from './posts/phase17'
import { phase18 } from './posts/phase18'
import { phase19 } from './posts/phase19'
import { phase20 } from './posts/phase20'
// phase21(산재보험료 환급)은 DB(API)로 발행 — TS 번들 미포함(무배포 발행 검증).
// 발행 소스는 _drafts/phase21.ts (posts/ 밖 → 사이트맵 slug 스캔 대상 아님, DB-only 글로 취급).

export const BLOG_POSTS: BlogPost[] = [
  ...phase1,
  ...phase2,
  ...phase3,
  ...phase4,
  ...phase5,
  ...phase6,
  ...phase7,
  ...phase8,
  ...phase9,
  ...phase10,
  ...phase11,
  ...phase12,
  ...phase13,
  ...phase14,
  ...phase15,
  ...phase16,
  ...phase17,
  ...phase18,
  ...phase19,
  ...phase20
// 이번 일괄 배포분 전체 날짜 숨김(기본 true). 개별 글에서 hideDate:false 로 표시 가능.
].map((p) => ({ hideDate: true, ...p }))

// ── UI 문자열 (index/article 공통) ────────────────────────────────────────────
export const BLOG_UI = {
  ko: {
    eyebrow: '산업안전 AI · 현장 노트',
    heroTitle: '위험성평가부터 안전교육까지, 산업안전 업무를 AI로',
    heroSub: 'Heinrich가 자동화하는 안전 실무 이야기 — 위험성평가·JSA·TBM·작업허가서·재해조사표·안전교육을 270만+ 실제 산업재해 데이터 위에서.',
    read: (m: number) => `${m}분 읽기`,
    min: (m: number) => `${m}분`,
    readMore: '이어서 읽기',
    latest: '최신 글',
    backToList: '블로그 전체 글',
    crumbBlog: '블로그',
    metaTitle: 'Heinrich 블로그 — 산업안전·데이터·AI 현장 노트',
    metaDesc: '안전관리자를 위한 실무 글. 위험성평가·JSA·TBM을 실제 사고 데이터에 근거하게 만드는 법 — 270만+ 정제 산업재해 데이터 기반.',
  },
  en: {
    eyebrow: 'Industrial safety AI · Field notes',
    heroTitle: 'From risk assessment to safety training — safety work, done by AI',
    heroSub: 'Field notes from Heinrich — how we automate risk assessments, JSAs, toolbox talks, work permits, incident reports and safety training, grounded in 2.7M+ real incidents.',
    read: (m: number) => `${m} min read`,
    min: (m: number) => `${m} min`,
    readMore: 'Read the article',
    latest: 'Latest',
    backToList: 'All articles',
    crumbBlog: 'Blog',
    metaTitle: 'Heinrich Blog — Field notes on safety, data & AI',
    metaDesc: 'Practical writing for EHS professionals: ground risk assessments, JSAs and toolbox talks in real accident data — from 2.7M+ refined records.',
  },
} as const

export function getPosts(lang: 'ko' | 'en'): BlogPost[] {
  return [...BLOG_POSTS]
    .filter((p) => p.lang === lang)
    .sort((a, b) => b.date.localeCompare(a.date))
}

export function getPost(slug: string, lang: 'ko' | 'en'): BlogPost | undefined {
  return BLOG_POSTS.find((p) => p.slug === slug && p.lang === lang)
    ?? BLOG_POSTS.find((p) => p.slug === slug)
}
